import React from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  CardMedia,
  Divider,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  Insights as InsightsIcon,
  Handshake as HandshakeIcon,
  Gavel as GavelIcon,
  Timeline as TimelineIcon
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Import our custom animation components
import TradingPlatformAnimation from '../components/TradingPlatformAnimation';
import TradingProcessAnimation from '../components/TradingProcessAnimation';
import MarketDataVisualization from '../components/MarketDataVisualization';
import BlockchainTraceability from '../components/BlockchainTraceability';
import ComplianceFeatures from '../components/ComplianceFeatures';

// Hero image (not currently used)
const heroImage = 'https://images.unsplash.com/photo-1518611012118-696072aa579a?ixlib=rb-4.0.3&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=1740&q=80';

/**
 * Public landing page component
 */
const LandingPage = () => {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Features list
  const features = [
    {
      icon: <SecurityIcon fontSize="large" color="primary" />,
      title: 'Secure Transactions',
      description: 'End-to-end encrypted transactions with multi-factor authentication and blockchain verification.'
    },
    {
      icon: <SpeedIcon fontSize="large" color="primary" />,
      title: 'Real-time Market Data',
      description: 'Access up-to-the-minute pricing, supply, and demand information for informed decision making.'
    },
    {
      icon: <InsightsIcon fontSize="large" color="primary" />,
      title: 'Advanced Analytics',
      description: 'Powerful analytics tools to track market trends, predict price movements, and optimize your trading strategy.'
    },
    {
      icon: <HandshakeIcon fontSize="large" color="primary" />,
      title: 'Direct Connections',
      description: 'Connect directly with verified miners, processors, and buyers without intermediaries.'
    },
    {
      icon: <GavelIcon fontSize="large" color="primary" />,
      title: 'Regulatory Compliance',
      description: 'Built-in compliance tools to ensure all transactions meet international regulations and standards.'
    },
    {
      icon: <TimelineIcon fontSize="large" color="primary" />,
      title: 'Supply Chain Tracking',
      description: 'Complete visibility into your supply chain from mine to market with detailed provenance tracking.'
    }
  ];

  // Import custom SVG icons for stakeholders
  const MinerIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="#ca8a04" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" fill="rgba(234, 179, 8, 0.2)"/>
      <path d="M2 17L12 22L22 17" stroke="#ca8a04" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M2 12L12 17L22 12" stroke="#ca8a04" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="12" cy="12" r="2" fill="#ca8a04"/>
    </svg>
  );

  const ManufacturerIcon = () => (
    <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19 21V7M5 21V11M12 21V3" stroke="#ca8a04" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <path d="M3 21H21" stroke="#ca8a04" strokeWidth="2" strokeLinecap="round"/>
      <path d="M9 8L12 5L15 8" stroke="#ca8a04" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
      <rect x="10" y="11" width="4" height="4" rx="1" fill="rgba(234, 179, 8, 0.2)" stroke="#ca8a04" strokeWidth="1.5"/>
    </svg>
  );

  // Stakeholder types
  const stakeholders = [
    {
      title: 'Miners & Producers',
      description: 'List your minerals directly to a global marketplace of verified buyers. Get fair market prices and secure payment processing.',
      icon: <MinerIcon />
    },
    {
      title: 'Processors & Manufacturers',
      description: 'Source raw materials with verified origin and quality. Ensure compliance with regulations and meet sustainability goals.',
      icon: <ManufacturerIcon />
    },
    {
      title: 'Traders & Brokers',
      description: 'Execute trades efficiently with reduced counterparty risk. Access a wider network of verified suppliers and buyers.',
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M3 3V21H21" stroke="#ca8a04" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <path d="M7 16L11 12L15 16L19 12" stroke="#ca8a04" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          <rect x="7" y="8" width="4" height="4" rx="1" fill="rgba(234, 179, 8, 0.2)" stroke="#ca8a04" strokeWidth="1.5"/>
          <rect x="15" y="6" width="4" height="4" rx="1" fill="rgba(234, 179, 8, 0.2)" stroke="#ca8a04" strokeWidth="1.5"/>
        </svg>
      )
    },
    {
      title: 'Regulators & Auditors',
      description: 'Monitor transactions for compliance with regulations. Verify mineral origins and track chain of custody.',
      icon: (
        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 21V12M12 12V3M12 12H3M12 12H21" stroke="#ca8a04" strokeWidth="2" strokeLinecap="round"/>
          <circle cx="12" cy="12" r="9" stroke="#ca8a04" strokeWidth="2" fill="rgba(234, 179, 8, 0.1)"/>
          <circle cx="12" cy="12" r="3" fill="rgba(234, 179, 8, 0.2)" stroke="#ca8a04" strokeWidth="1.5"/>
        </svg>
      )
    }
  ];

  return (
    <Box>
      {/* Hero Section - Styled like Laprecio.com */}
      <Box
        sx={{
          background: 'linear-gradient(to right, #fffde7, #fff9c4)',
          color: '#333333',
          py: 12,
          textAlign: 'center'
        }}
        className="hero-gradient"
      >
        <Container maxWidth="lg">
          <Typography variant="h2" component="h1" gutterBottom fontWeight="bold" sx={{ fontSize: { xs: '2.5rem', md: '3.5rem' } }}>
            Global Trade. <Box component="span" sx={{ color: '#ca8a04' }}>Digital Trust.</Box>
          </Typography>
          <Typography variant="h5" component="h2" gutterBottom sx={{ mb: 6, maxWidth: '800px', mx: 'auto', color: '#555555', fontSize: { xs: '1.2rem', md: '1.5rem' } }}>
            Where ethical sourcing meets AI, blockchain and sustainable value creation.
          </Typography>
          <Box sx={{ mt: 6 }}>
            {user ? (
              <Button
                component="a"
                href="https://app.laprecio.com/dashboard"
                variant="contained"
                size="large"
                className="laprecio-button"
                sx={{ mr: 2, px: 5, py: 1.8, borderRadius: 50, fontSize: '1.1rem', fontWeight: 'bold' }}
              >
                Go to Dashboard
              </Button>
            ) : (
              <>
                <Button
                  component={RouterLink}
                  to="/register"
                  variant="contained"
                  size="large"
                  className="laprecio-button"
                  sx={{ mr: 3, px: 5, py: 1.8, borderRadius: 50, fontSize: '1.1rem', fontWeight: 'bold' }}
                >
                  Join Beta
                </Button>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="outlined"
                  size="large"
                  color="primary"
                  sx={{ px: 5, py: 1.8, borderRadius: 50, fontSize: '1.1rem', fontWeight: 'bold' }}
                >
                  Sign In
                </Button>
              </>
            )}
          </Box>
        </Container>
      </Box>

      {/* Trading Platform Animation Section */}
      <Container maxWidth="lg" sx={{ py: 8 }} className="laprecio-section">
        <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold' }}>
          Minerals Trading Platform
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph sx={{ mb: 6 }}>
          Experience our innovative platform that connects miners, traders, and manufacturers
        </Typography>

        {/* Trading Platform Animation */}
        <TradingPlatformAnimation />

        {/* Trading Process Animation */}
        <TradingProcessAnimation />

        {/* Market Data Visualization */}
        <MarketDataVisualization />

        {/* Blockchain Traceability */}
        <BlockchainTraceability />

        {/* Compliance Features */}
        <ComplianceFeatures />

        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 'bold' }}>
            Ready to transform your mineral trading experience?
          </Typography>
          <Typography variant="body1" paragraph sx={{ maxWidth: 700, mx: 'auto', mb: 3 }}>
            Join our platform today and experience the future of secure, transparent, and efficient mineral trading.
          </Typography>
          <Button
            component={RouterLink}
            to="/register"
            variant="contained"
            size="large"
            className="laprecio-button"
            sx={{ px: 4, py: 1.5, borderRadius: 50, fontSize: '1rem', fontWeight: 'bold' }}
          >
            Register Interest
          </Button>
        </Box>
      </Container>

      {/* Features Section */}
      <Container maxWidth="lg" sx={{ py: 10 }} className="laprecio-section">
        <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold' }}>
          Platform Features
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph sx={{ mb: 8 }}>
          Our comprehensive platform offers everything you need for secure and efficient mineral trading
        </Typography>

        <Grid container spacing={4}>
          {features.map((feature, index) => (
            <Grid item xs={12} sm={6} md={4} key={index}>
              <Card
                sx={{
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 3,
                  border: '2px solid #f5e5a5',
                  borderLeft: '6px solid #eab308',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.15)'
                  }
                }}
                className="laprecio-card"
              >
                <CardContent sx={{ flexGrow: 1, textAlign: 'center', p: 4 }}>
                  <Box sx={{ mb: 3, display: 'flex', justifyContent: 'center' }}>
                    {React.cloneElement(feature.icon, { style: { fontSize: 60 } })}
                  </Box>
                  <Typography gutterBottom variant="h5" component="h3" sx={{ fontWeight: 'bold', mb: 2 }}>
                    {feature.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {feature.description}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works Section */}
      <Box sx={{ bgcolor: '#fffef0', py: 10 }} className="laprecio-section-alt">
        <Container maxWidth="lg">
          <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold' }}>
            How It Works
          </Typography>
          <Typography variant="h6" align="center" color="text.secondary" paragraph sx={{ mb: 8 }}>
            Our platform connects all stakeholders in the mineral supply chain
          </Typography>

          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '12px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                  backgroundColor: '#fffef0',
                  minHeight: '350px',
                  border: '2px solid #f5e5a5',
                  borderLeft: '6px solid #eab308',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.2)'
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mb: 3
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: '#ca8a04',
                      width: 100,
                      height: 100,
                      mb: 3,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    <TimelineIcon sx={{ fontSize: 60 }} />
                  </Avatar>
                  <Typography variant="h4" align="center" gutterBottom>
                    Mine to Market
                  </Typography>
                  <Typography variant="body1" align="center" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Complete tracking from extraction to final delivery
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom>
                From Mine to Market
              </Typography>
              <Typography variant="body1" paragraph>
                Our platform tracks minerals from their source, ensuring complete transparency and compliance with international regulations. Miners can register their operations, document extraction processes, and certify the origin of their minerals.
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Verified mine registration and certification" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Documented extraction and processing" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Blockchain-verified chain of custody" />
                </ListItem>
              </List>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 6 }} />
            </Grid>

            <Grid item xs={12} md={6} sx={{ order: { xs: 2, md: 1 } }}>
              <Typography variant="h4" gutterBottom>
                Secure Trading Platform
              </Typography>
              <Typography variant="body1" paragraph>
                Our trading platform connects verified buyers and sellers, facilitating secure transactions with escrow services, quality assurance, and logistics coordination. All transactions are recorded on a distributed ledger for maximum security and transparency.
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Verified buyer and seller profiles" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Secure payment processing and escrow" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Quality assurance and dispute resolution" />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6} sx={{ order: { xs: 1, md: 2 } }}>
              <Paper
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '12px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                  backgroundColor: '#fffef0',
                  minHeight: '350px',
                  border: '2px solid #f5e5a5',
                  borderLeft: '6px solid #eab308',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.2)'
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mb: 3
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: '#ca8a04',
                      width: 100,
                      height: 100,
                      mb: 3,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    <SecurityIcon sx={{ fontSize: 60 }} />
                  </Avatar>
                  <Typography variant="h4" align="center" gutterBottom>
                    Secure Trading
                  </Typography>
                  <Typography variant="body1" align="center" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Encrypted transactions with blockchain verification
                  </Typography>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 6 }} />
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                sx={{
                  p: 4,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '12px',
                  boxShadow: '0 10px 30px rgba(0,0,0,0.15)',
                  backgroundColor: '#fffef0',
                  minHeight: '350px',
                  border: '2px solid #f5e5a5',
                  borderLeft: '6px solid #eab308',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 15px 35px rgba(0,0,0,0.2)'
                  }
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    mb: 3
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: '#ca8a04',
                      width: 100,
                      height: 100,
                      mb: 3,
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                    }}
                  >
                    <GavelIcon sx={{ fontSize: 60 }} />
                  </Avatar>
                  <Typography variant="h4" align="center" gutterBottom>
                    Compliance & Reporting
                  </Typography>
                  <Typography variant="body1" align="center" color="text.secondary" sx={{ fontSize: '1.1rem' }}>
                    Automated regulatory compliance and ESG reporting
                  </Typography>
                </Box>
              </Paper>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h4" gutterBottom>
                Compliance & Reporting
              </Typography>
              <Typography variant="body1" paragraph>
                Our platform ensures compliance with international regulations such as the EU Conflict Minerals Regulation, OECD Due Diligence Guidance, and the US Dodd-Frank Act. Generate comprehensive reports for regulatory submissions and sustainability reporting.
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Automated compliance checking" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Regulatory reporting templates" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CheckCircleIcon color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="ESG and sustainability metrics" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Container>
      </Box>

      {/* Stakeholders Section */}
      <Container maxWidth="lg" sx={{ py: 10 }} className="laprecio-section">
        <Typography variant="h3" component="h2" align="center" gutterBottom sx={{ fontWeight: 'bold' }}>
          For All Stakeholders
        </Typography>
        <Typography variant="h6" align="center" color="text.secondary" paragraph sx={{ mb: 8 }}>
          Our platform serves the needs of everyone in the mineral supply chain
        </Typography>

        <Grid container spacing={4}>
          {stakeholders.map((stakeholder, index) => (
            <Grid item xs={12} sm={6} key={index}>
              <Paper
                sx={{
                  p: 4,
                  height: '100%',
                  borderRadius: 3,
                  border: '2px solid #f5e5a5',
                  borderLeft: '6px solid #eab308',
                  boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
                  transition: 'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-8px)',
                    boxShadow: '0 12px 30px rgba(0,0,0,0.15)'
                  }
                }}
                className="laprecio-card"
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                  <Box
                    sx={{
                      bgcolor: 'white',
                      width: 80,
                      height: 80,
                      mr: 3,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      borderRadius: '50%',
                      border: '2px solid #eab308',
                      boxShadow: '0 6px 16px rgba(234, 179, 8, 0.25)'
                    }}
                  >
                    {stakeholder.icon}
                  </Box>
                  <Typography variant="h5" component="h3" sx={{ fontWeight: 'bold' }}>
                    {stakeholder.title}
                  </Typography>
                </Box>
                <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.05rem', lineHeight: 1.6 }}>
                  {stakeholder.description}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* CTA Section */}
      <Box sx={{ bgcolor: '#fffef0', color: '#333333', py: 10 }} className="laprecio-section-alt">
        <Container maxWidth="md" sx={{ textAlign: 'center' }}>
          <Typography variant="h3" component="h2" gutterBottom sx={{ fontWeight: 'bold' }}>
            Ready to Transform Your Mineral Trading?
          </Typography>
          <Typography variant="h6" paragraph sx={{ mb: 5, color: '#555555' }}>
            Register your interest in our upcoming platform for secure, transparent, and efficient mineral trading
          </Typography>
          {user ? (
            <Button
              component="a"
              href="https://app.laprecio.com/dashboard"
              variant="contained"
              size="large"
              className="laprecio-button"
              sx={{ px: 5, py: 1.8, borderRadius: 50, fontSize: '1.1rem', fontWeight: 'bold' }}
            >
              Go to Dashboard
            </Button>
          ) : (
            <Button
              component={RouterLink}
              to="/register"
              variant="contained"
              size="large"
              className="laprecio-button"
              sx={{ px: 5, py: 1.8, borderRadius: 50, fontSize: '1.1rem', fontWeight: 'bold' }}
            >
              Register Interest
            </Button>
          )}
        </Container>
      </Box>


    </Box>
  );
};

export default LandingPage;
